/**
 * Der Host-Bildschirm — §42, §48, §67 Schritte 4 bis 18.
 *
 * **Konfiguration und Lobby sind ein Bildschirm** (Nr. 27, `GAME_RULES.md` Abschnitt 12). Solange
 * es keine Partie gibt, steht hier das Formular; danach die Lobby mit dem Einladungslink und den
 * Bedienelementen. Die Konfiguration bleibt bis zum Start veränderbar — die Lobby füllt sich,
 * während der Host noch konfiguriert.
 *
 * **Die Lobby kommt über den WebSocket, nicht über HTTP.** Nach der Anmeldung schickt der Server
 * Zustand, Lobby und Anwesenheit von selbst und danach jede Änderung (`realtime/handler.ts`) —
 * „Host sieht Teilnehmer" (Schritt 17) ist damit **live** und nicht ein Abruf. Ein zusätzlicher
 * `GET /games/lobby` wäre dieselbe Antwort, nur älter.
 *
 * **Der Einladungslink wird im Browser behalten** (`invite.ts`). Sein Klartext existiert genau
 * einmal; ohne das kostet jedes Neuladen genau den Link, den der Host gerade verschicken wollte.
 * Fehlt er — anderes Gerät, geleerter Speicher —, erzeugt der Host einen neuen; der alte verfällt.
 *
 * **Eine beendete Partie führt zurück zum Formular.** Derselbe Host-Link legt die nächste Partie
 * an (`CLAUDE.md`, Berechtigungsmodell); ohne das säße der Host vor einer Lobby, die vorbei ist.
 *
 * **Eine abgewiesene Sitzung wird vergessen, nicht wiederholt.** `useConnection` räumt den
 * gespeicherten Token weg; dieser Bildschirm zieht nach und fragt wieder nach einer neuen Partie.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CreateGameRequest, GameDefaults, LobbyView, SectorIdDto } from '@flaghex/contracts';

import { useRequest } from '../api/context.js';
import {
  abortGame,
  addBot,
  assignSector,
  createGame,
  currentGame,
  gameDefaults,
  removePlayer,
  renewInviteToken,
  startGame,
} from '../api/endpoints.js';
import type { ApiFailure, ApiResult } from '../api/transport.js';
import { hostLink, readInvite, writeInvite } from '../invite.js';
import { CreateGameForm } from '../lobby/CreateGameForm.js';
import { ConfirmButton } from '../game/ConfirmButton.js';
import { GamePanel } from '../game/GamePanel.js';
import { LobbyPanel } from '../lobby/LobbyPanel.js';
import { useConnection } from '../realtime/useConnection.js';
import { readSession, writeSession } from '../session.js';

export interface HostScreenProps {
  readonly hostToken: string;
}

export function HostScreen({ hostToken }: HostScreenProps) {
  const { t } = useTranslation(['lobby', 'errors', 'common', 'status', 'play']);
  const request = useRequest();

  const [session, setSession] = useState<string | undefined>(() => readSession());
  const [owned, setOwned] = useState<LobbyView | undefined>(undefined);
  const [asked, setAsked] = useState(false);
  /** Der Abruf selbst ist schiefgegangen — etwas anderes als „es gibt keine Partie". */
  const [lookupFailure, setLookupFailure] = useState<ApiFailure | undefined>(undefined);
  const [invite, setInvite] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<ApiFailure | undefined>(undefined);
  /**
   * Die Vorgaben für das Formular (`R10`). **Ohne sie gibt es kein Formular**, sondern einen
   * neuen Versuch: leere Felder wären genau der Fehler, den `R10` behebt.
   */
  const [defaults, setDefaults] = useState<GameDefaults | undefined>(undefined);
  const [defaultsFailure, setDefaultsFailure] = useState<ApiFailure | undefined>(undefined);

  const { view, status, send } = useConnection(session);


  /**
   * Welche Partie zu **diesem Host-Link** gehört — gefragt wird der Dienst, nicht der Browser.
   *
   * **Nur `NOT_FOUND` heißt „der Link ist frei".** Jede andere Antwort heißt „ich weiß es
   * gerade nicht", und das ist etwas anderes: während eines Serverneustarts antwortete der
   * Entwicklungs-Proxy mit 502, und der Bildschirm bot an, eine **zweite** Partie anzulegen —
   * die der Server dann mit `ACTIVE_GAME_EXISTS` abgewiesen hätte. Gefunden bei der
   * Sichtprüfung im Browser.
   *
   * Eine beendete Partie zählt serverseitig nicht mehr als aktiv und führt damit von selbst
   * zurück zum Formular.
   */
  const refresh = useCallback(async (): Promise<void> => {
    const result = await currentGame(request, hostToken);
    if (result.kind === 'ok') {
      setOwned(result.value);
      setLookupFailure(undefined);
    } else if (result.code === 'NOT_FOUND') {
      setOwned(undefined);
      setLookupFailure(undefined);
    } else {
      setLookupFailure(result);
    }
    setAsked(true);
  }, [request, hostToken]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const loadDefaults = useCallback(async (): Promise<void> => {
    const result = await gameDefaults(request, hostToken);
    if (result.kind === 'ok') {
      setDefaults(result.value);
      setDefaultsFailure(undefined);
    } else {
      setDefaultsFailure(result);
    }
  }, [request, hostToken]);

  useEffect(() => {
    void loadDefaults();
  }, [loadDefaults]);

  // `useConnection` vergisst den Token bei einer abgewiesenen Anmeldung; hier wird nachgezogen.
  useEffect(() => {
    if (status === 'unauthenticated') {
      setSession(undefined);
    }
  }, [status]);

  // Eine Sitzung, die zu einer **anderen** Partie gehört, wird beiseitegelegt — sonst säße der
  // Host im Raum einer fremden Partie und bekäme deren Nachrichten.
  useEffect(() => {
    if (owned !== undefined && view.gameId !== undefined && view.gameId !== owned.gameId) {
      setSession(undefined);
    }
  }, [owned, view.gameId]);

  // Die Lobby aus dem WebSocket, sobald sie **dieselbe** Partie betrifft; sonst die abgefragte.
  // So ist die Anzeige live, wenn der Host mitspielt, und richtig, wenn er es nicht tut.
  const lobby = view.lobby?.gameId === owned?.gameId ? (view.lobby ?? owned) : owned;

  // Der behaltene Link gilt **dieser** Partie; für eine andere ist er keiner.
  useEffect(() => {
    setInvite(lobby === undefined ? undefined : readInvite(lobby.gameId));
  }, [lobby]);

  const create = useCallback(
    (body: CreateGameRequest): void => {
      setFailure(undefined);
      void createGame(request, hostToken, body).then((result) => {
        if (result.kind !== 'ok') {
          setFailure(result);
          return;
        }
        // Beide Geheimnisse verlassen den Server genau einmal — sofort behalten.
        writeSession(result.value.sessionToken);
        writeInvite(result.value.gameId, result.value.inviteToken);
        setInvite(result.value.inviteToken);
        setSession(result.value.sessionToken);
        void refresh();
      });
    },
    [request, hostToken, refresh],
  );

  /** Ein Hostbefehl: läuft er schief, steht der Grund an einer Stelle. */
  const command = useCallback(
    (run: Promise<ApiResult<unknown>>): void => {
      setFailure(undefined);
      void run.then((result) => {
        if (result.kind !== 'ok') {
          setFailure(result);
          return;
        }
        // Nachfragen, damit die Anzeige auch dann stimmt, wenn der Host nicht mitspielt und
        // deshalb keine Lobby-Nachrichten bekommt.
        void refresh();
      });
    },
    [refresh],
  );

  const renew = useCallback((): void => {
    if (lobby === undefined) return;
    setFailure(undefined);
    void renewInviteToken(request, hostToken, lobby.gameId).then((result) => {
      if (result.kind !== 'ok') {
        setFailure(result);
        return;
      }
      writeInvite(lobby.gameId, result.value.inviteToken);
      setInvite(result.value.inviteToken);
    });
  }, [request, hostToken, lobby]);

  if (!asked) {
    return (
      <main>
        <h1>{t('lobby:hostHeading')}</h1>
        <p role="status">{t('common:loading')}</p>
      </main>
    );
  }

  if (lookupFailure !== undefined) {
    return (
      <main>
        <h1>{t('lobby:hostHeading')}</h1>
        <p role="alert">{t(`errors:${lookupFailure.code}`)}</p>
        <p>
          <button type="button" onClick={() => void refresh()}>
            {t('common:retry')}
          </button>
        </p>
      </main>
    );
  }

  if (lobby === undefined) {
    if (defaults === undefined) {
      return (
        <main>
          <h1>{t('lobby:hostHeading')}</h1>
          {defaultsFailure === undefined ? (
            <p role="status">{t('common:loading')}</p>
          ) : (
            <>
              <p role="alert">{t(`errors:${defaultsFailure.code}`)}</p>
              <p>
                <button type="button" onClick={() => void loadDefaults()}>
                  {t('common:retry')}
                </button>
              </p>
            </>
          )}
        </main>
      );
    }
    return (
      <main>
        <h1>{t('lobby:hostHeading')}</h1>
        <CreateGameForm onCreate={create} defaults={defaults} failure={failure} />
      </main>
    );
  }

  /**
   * **Ab `SETUP` entscheidet der Status, nicht mehr das Fragment** — die Zustandsmaschine aus
   * **E26**: welcher Bildschirm gilt, folgt aus Token *und* Partiestand.
   *
   * **Der Stand kommt aus dem Zustand, nicht aus der Lobby-Ansicht.** Der Übergang
   * `SETUP → PLAYING` passiert in der Spielsitzung und schickt einen neuen Zustand, aber keine
   * neue Lobby — wer sie liest, zeigt nach dem letzten Bestätigen weiter „Aufstellung läuft",
   * während das Brett schon alle Figuren zeigt. Gefunden bei der Sichtprüfung im Browser. Die
   * Lobby-Ansicht bleibt der Rückfall für den Host **ohne** eigene Sitzung: er hat dann keinen
   * Zustand, aber einen Stand.
   */
  const phase = view.state?.status ?? lobby.status;
  if (phase !== 'CREATED' && phase !== 'LOBBY') {
    /*
      Der Spielbildschirm bringt seit Zyklus D2b seine **eigene** Kopfleiste mit: Phase, Stand und
      wer am Zug ist. Eine Überschrift des Bildschirms darüber wäre eine zweite — genau daran ist
      in Zyklus 10g die Sichtprüfung hängengeblieben („Partie einrichten" über einer laufenden
      Partie).
    
      Der Rückfall bleibt für den Fall **ohne eigenen Zustand**: der Host, der die Partie angelegt
      hat, aber nicht mitspielt. `GamePanel` zeigt dann nur „wird geladen"; die Phase steht
      trotzdem in der Lobby-Ansicht.
    */
    const onAbort = (): void => command(abortGame(request, hostToken, lobby.gameId));
    return view.state === undefined ? (
      <main>
        <h1>{t('lobby:playingHeading')}</h1>
        <p role="status">{t('lobby:statusLabel', { status: t(`status:${phase}`) })}</p>
        {/* Ohne eigenen Zustand zeigt `GamePanel` keine Kopfleiste — der Knopf steht deshalb
            hier (`R12`). Genau der Host, der Bots gegeneinander spielen lässt, braucht ihn. */}
        {phase === 'SETUP' || phase === 'PLAYING' || phase === 'PAUSED' ? (
          <p>
            <ConfirmButton
              label={t('play:abort')}
              confirmLabel={t('play:abortConfirm')}
              onConfirm={onAbort}
            />
          </p>
        ) : null}
        <GamePanel view={view} send={send} onAbort={onAbort} />
      </main>
    ) : (
      <GamePanel view={view} send={send} onAbort={onAbort} />
    );
  }

  const assign = (playerId: string, sector: SectorIdDto | null): void =>
    command(assignSector(request, hostToken, lobby.gameId, playerId, sector));

  return (
    <main>
      <h1>{t('lobby:hostHeading')}</h1>

      <section>
        <h2>{t('lobby:invite.heading')}</h2>
        {invite === undefined ? (
          <p>{t('lobby:invite.missing')}</p>
        ) : (
          <>
            <p>
              <code>{hostLink(window.location.origin, invite)}</code>
            </p>
            <p>{t('lobby:invite.hint')}</p>
            <p>{t('lobby:invite.once')}</p>
          </>
        )}
        <p>
          <button type="button" onClick={renew}>
            {t('lobby:invite.renew')}
          </button>
        </p>
      </section>

      {failure === undefined ? null : (
        <div role="alert">
          <p>{t('lobby:host.failed')}</p>
          <p>{t(`errors:${failure.code}`)}</p>
          {failure.message === '' ? null : <p>{failure.message}</p>}
        </div>
      )}

      <LobbyPanel
        lobby={lobby}
        connected={view.connectedPlayers}
        host={{
          onAssign: assign,
          onRemove: (playerId) =>
            command(removePlayer(request, hostToken, lobby.gameId, playerId)),
          onStart: () => command(startGame(request, hostToken, lobby.gameId)),
          // Über `command` wie jeder Hostbefehl: schlägt es fehl, steht der Grund an **einer**
          // Stelle — und das ist hier wichtiger als bei den anderen, weil der Dienst eine
          // unausführbare Strategie oder einen belegten Platz unterscheidbar abweist
          // (**E65**: `INVALID_CONFIGURATION`, `SECTOR_TAKEN`, `SLOT_NOT_CONFIGURED`).
          onAddBot: (sector, displayName, choice) =>
            command(addBot(request, hostToken, lobby.gameId, { displayName, sector, choice })),
          onClose: () => command(abortGame(request, hostToken, lobby.gameId)),
        }}
      />
    </main>
  );
}
